import React from 'react'
import { UserStore } from '../store/userStore'
import { useAxios } from '../hooks/useAxios'
import { getAnnouncements } from '../api/getAnnouncements'
import { toast } from 'react-toastify'
import getArticles from '../api/getArticles'
import Card from '../components/Card'
import Skeleton from 'react-loading-skeleton'
import AnnouncementCard from '../components/AnnouncementCard'
import Btn from '../components/Btn'
import { ReactSVG } from 'react-svg'
import Articles from '../components/Articles'
import ArticleCard from '../components/ArticleCard'
import { ArticleData } from '../server/utils/getArticlesData'
import { favoriteHandler } from '../utils/favoriteHandler'
import IconTextBlock from '../components/IconTextBlock'
import Person from '../components/Person'
import Balls from '../components/Balls'
import { useMediaQuery } from 'react-responsive'

const MainPage: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const { response: ads, fire: adsFire } = useAxios()
  const { response: rating, fire: ratingFire } = useAxios()
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)'
  })

  React.useEffect(() => {
    const handler = async () => {
      setLoading(true)

      const ads = adsFire(getAnnouncements()).then(({ error }) => {
        if (error) {
          toast.error(error.message)
        }
      })

      const rating = ratingFire(getArticles('/rating/list', { count: 3 })).then(
        ({ error }) => {
          if (error) {
            toast.error(error.message)
          }
        }
      )

      await Promise.all([ads, rating])

      setLoading(false)
    }

    handler()
  }, [])

  return (
    <div className={'page'}>
      <header className="page__head">
        <h1>{`Здравствуйте, ${UserStore.data.name}`}</h1>
      </header>

      <div className="page__content">
        {loading || ads?.data?.length || rating?.data ? (
          <div className={'app-grid _columns-2-1 mb-md-14 mb-sm-12'}>
            {loading ? (
              <>
                {Array.from({ length: 2 }).map((_, index) => (
                  <Card
                    key={index}
                    style={{
                      width: 'auto',
                      height: isMobile ? '20rem' : '37.6rem'
                    }}
                    className={'u-flex u-flexCol'}
                  >
                    <header className={'card-head d-block'}>
                      <Skeleton width={'50%'}></Skeleton>
                    </header>

                    <div className={'card-content u-flexGrow1'}>
                      <Skeleton count={1} height={'100%'}></Skeleton>
                    </div>
                  </Card>
                ))}
              </>
            ) : (
              <>
                {ads?.data?.map((item: any) => (
                  <AnnouncementCard key={item.id} {...item}></AnnouncementCard>
                ))}
                {rating?.data ? (
                  <AnnouncementCard
                    pretitle={'Рейтинг'}
                    headLink={'/rating'}
                    headLinkText={'Весь рейтинг'}
                    className={'announcement-rating-card'}
                    render={() => {
                      return (
                        <>
                          <div className="announcement-rating-card__self">
                            <div className="announcement-rating-card__self-picture">
                              <img src={'/i/icon.png'} alt={'icon'} />
                            </div>
                            <div className="announcement-rating-card__self-content">
                              <p
                                className={
                                  'text-caption text-secondary-color mb-2'
                                }
                              >
                                Ваше место в рейтинге
                              </p>
                              <p className={'text-h2'}>
                                {rating.data?.data.user.position
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                              </p>
                              <Balls
                                className={
                                  'text-secondary-color mt-md-4 mt-sm-1'
                                }
                                text
                                balls={rating.data?.data.user.balls}
                                icon="/i/svg/rating.svg"
                              ></Balls>
                            </div>
                          </div>
                          {rating?.data?.data.items.length ? (
                            <div className="announcement-rating-card__items">
                              {rating?.data?.data.items.map((item: any) => (
                                <Person
                                  id={item.id}
                                  name={item.name}
                                  key={item.id}
                                  sm
                                  avatarUrl={item.avatarUrl}
                                  render={() => (
                                    <Balls
                                      className={'text-secondary-color'}
                                      icon="/i/svg/rating.svg"
                                      balls={item.balls}
                                    ></Balls>
                                  )}
                                ></Person>
                              ))}
                            </div>
                          ) : null}
                        </>
                      )
                    }}
                  ></AnnouncementCard>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        <div className="app-section mb-md-14 mb-sm-12">
          <div className="app-section__head">
            <h2>Мероприятия</h2>

            <Btn to="/events" text>
              <span>Все мероприятия</span>
              <ReactSVG src={`/i/svg/arrow-right.svg`} wrapper={'span'} />
            </Btn>
          </div>

          <div className="app-section__content">
            <Articles
              filtersHidden
              noPagination
              contentGutterSmall
              count={3}
              method={(options) => getArticles('/events/list', options)}
              renderContent={({ items }, loading, updateSections) => {
                return loading || items.length ? (
                  <div className="app-grid _columns-3-1">
                    {loading
                      ? Array.from({ length: 3 }).map((_, index) => {
                          return <ArticleCard key={index} loading />
                        })
                      : items.map((item: ArticleData) => (
                          <ArticleCard
                            favoriteHandler={async (value) => {
                              await favoriteHandler(
                                value,
                                item.id as string,
                                'events'
                              )
                              await updateSections()
                            }}
                            key={item.id}
                            tags={item.tags || []}
                            title={item.title}
                            picture={
                              item.previewPicture || item.detailPicture || ''
                            }
                            startDate={item.startDate || ''}
                            favorite={item.favorite}
                            to={`/events/${item.id}`}
                            renderFoot={() => (
                              <IconTextBlock list={item.informationItems} />
                            )}
                          />
                        ))}
                  </div>
                ) : (
                  'Нет данных'
                )
              }}
            />
          </div>
        </div>

        <div className="app-section">
          <div className="app-section__head">
            <h2>Сообщества</h2>

            <Btn to="/communities" text>
              <span>Все сообщества</span>
              <ReactSVG src={`/i/svg/arrow-right.svg`} wrapper={'span'} />
            </Btn>
          </div>

          <div className="app-section__content">
            <Articles
              tabsNavigation
              noPagination
              noFilters
              contentGutterSmall
              count={3}
              method={(options) => getArticles('/communities/list', options)}
              renderContent={({ items }, loading, updateSections) => {
                return loading ? (
                  <div className={'app-grid _columns-3-1'}>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <ArticleCard key={index} loading></ArticleCard>
                    ))}
                  </div>
                ) : items.length ? (
                  <div className={'app-grid _columns-3-1'}>
                    {items.map((item: ArticleData) => (
                      <ArticleCard
                        key={item.id}
                        favoriteHandler={async (value) => {
                          await favoriteHandler(value, item.id as string)
                          await updateSections()
                        }}
                        tags={item.tags}
                        title={item.title}
                        favorite={item.favorite}
                        picture={item.previewPicture}
                        to={`/communities/${item.id}`}
                        renderFoot={
                          item.chief
                            ? () => (
                                <Person
                                  avatarUrl={item.chief?.avatarUrl}
                                  name={item.chief?.name ?? ''}
                                  role={item.chief?.role}
                                ></Person>
                              )
                            : undefined
                        }
                      ></ArticleCard>
                    ))}
                  </div>
                ) : (
                  'Нет данных'
                )
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default MainPage
